import { buildDeliveryAvailability, dataLayerPush, getStoreId } from '@libs/analytics';
import type { Product, ProductApiResponse, TailoredApiResponse } from '@libs/common-types';
import { getCurrency } from '@libs/core';

import { type MiniPdpForm, MiniPdpSource } from '../types';

type MiniPdpAddToCartInput = {
    base: ProductApiResponse['data'];
    cartItemCount: number;
    formData: MiniPdpForm;
    selectedVariant: Product;
    source?: MiniPdpSource;
    tailored: TailoredApiResponse['data'] | null;
};

const YMAL_SOURCE = {
    itemListId: 'standard pdp',
    itemListName: 'you may also like',
    pdpType: 'mini pdp',
    productRecommendation: 'yes',
} as const;

export const mapMiniPdpAddToCartEvent = ({
    base,
    cartItemCount,
    formData,
    selectedVariant,
    source,
    tailored,
}: MiniPdpAddToCartInput) => {
    const { selectedSku, fulfilmentMethod, quantity } = formData;
    const price = selectedVariant.price.sale ?? selectedVariant.price.original;
    const hierarchy = base.merchandisingHierarchy;
    const variantValues = selectedVariant.variantValues;
    const sourceValues = source === MiniPdpSource.YMAL ? YMAL_SOURCE : undefined;

    return {
        event: 'add_to_cart',
        ecommerce: {
            currency: getCurrency(),
            value: price * quantity,
            pdp_type: sourceValues?.pdpType ?? '',
            category: base.categories[0] ?? '',
            sub_category1: base.categories[1] ?? '',
            sub_category2: base.categories[2] ?? '',
            add_to_cart_type: 'quick add',
            product_recommendation: sourceValues?.productRecommendation ?? 'no',
            items: [
                {
                    item_name: selectedVariant.name,
                    item_id: base.webStyleId,
                    quantity,
                    price,
                    item_category: hierarchy?.vicePresidentName ?? '',
                    item_category2: hierarchy?.division ?? '',
                    item_category3: hierarchy?.department ?? '',
                    item_category4: hierarchy?.class ?? '',
                    item_category5: hierarchy?.category ?? '',
                    item_brand: selectedVariant.brand?.name ?? '',
                    item_variant: selectedSku,
                    item_list_name: sourceValues?.itemListName ?? '',
                    item_list_id: sourceValues?.itemListId ?? '',
                    fulfillment_method: `${fulfilmentMethod} one-time`,
                    store_id: getStoreId({ fulfilmentMethod, tailoredVariant: tailored?.variants[selectedSku] }),
                    color: variantValues?.color ?? '',
                    size: variantValues?.size ?? '',
                    flavor: variantValues?.flavor ?? '',
                    item_search_term: null,
                    default_version: selectedSku === base.defaultSku ? 'yes' : 'no',
                    item_cart_order: cartItemCount + 1,
                    add_to_cart_type: 'quick add',
                    ...buildDeliveryAvailability({
                        deliveryMethods: selectedVariant.deliveryMethods,
                        tailoredVariant: tailored?.variants[selectedSku],
                        purchaseOption: 'one-time',
                        isCustomPharmacyOOS: false,
                    }),
                },
            ],
        },
    };
};

export const trackMiniPdpAddToCart = (input: MiniPdpAddToCartInput) => {
    try {
        const event = mapMiniPdpAddToCartEvent(input);

        dataLayerPush({ ecommerce: null });
        dataLayerPush(event);
    } catch {
        // Analytics failures do not block add to cart success
    }
};
